import type { HistogramTooltipDataPoint } from './bins-to-tooltip.js';
import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { getGroupedDataPointsByCoordinate } from '../../../../core/utils/group-by-threshold.js';
import { safeGet } from '../../../providers/HistogramDataProvider.js';
import {
  HistogramSeries,
  WeakMapBins,
  WeakMapSeries,
  XScale,
  YScale,
} from '../../../types/histogram.js';

/**
 * loop the histogram series and its bins to determine the closest X bins
 *
 * @param mouseX - the x mouse position
 * @param series - histogram series
 * @param yScales - yScales array
 * @param xScale - xScale
 * @param seriesMap - series weakmap
 * @param binsMap - bins weakmap
 *
 * @returns get the closest X bins information.
 */
export function findClosestBinX(
  mouseX: number,
  series: HistogramSeries[],
  yScales: YScale[],
  xScale: XScale,
  seriesMap: WeakMapSeries,
  binsMap: WeakMapBins,
) {
  let closestBinsX: HistogramTooltipDataPoint[] = [];
  let smallestXDelta = Infinity;

  for (const singleSeries of series) {
    const yScale = yScales[safeGet(seriesMap, singleSeries).scaleIndex];

    for (const bin of singleSeries.bins) {
      if (bin.from <= mouseX && bin.to >= mouseX) {
        const binMiddle = (bin.to - bin.from) / 2;
        const distance = Math.abs(mouseX - binMiddle);

        if (distance < smallestXDelta) {
          closestBinsX = [];
          smallestXDelta = distance;
        }

        if (distance === smallestXDelta) {
          const { cumulativeValue, value, seriesId, color } = safeGet(
            binsMap,
            bin,
          );
          const y = yScale(cumulativeValue ? cumulativeValue : value ?? 0) ?? 0;

          closestBinsX.push({
            id: seriesId,
            x: xScale(bin.from + (bin.to - bin.from) / 2) + OVERLAY_PADDING,
            y: y + OVERLAY_PADDING,
            color,
            bin,
          });
        }
      }
    }
  }

  return closestBinsX;
}

/**
 * Get the closest bins information: the closestBin base on the mouse y position, get the closestBins from the closest one and group the closestBins
 *
 * @param mouseY - the y mouse position
 * @param closestBinsX - closest bins regarding the x
 * @returns get the closest bins information.
 */
export function getClosestBins(
  mouseY: number,
  closestBinsX: HistogramTooltipDataPoint[],
) {
  // new object needed to preserve the points sorting
  const closestBin = [...closestBinsX].sort(
    (a, b) => Math.abs(a.y - mouseY) - Math.abs(b.y - mouseY),
  )[0];

  const closestBinsByClosestOne = closestBinsX.filter(
    (c) => c.bin.to === closestBin.bin.to && c.bin.from === closestBin.bin.from,
  );

  const closestBinsGrouped = getGroupedDataPointsByCoordinate(
    closestBinsByClosestOne,
    'y',
  ) as HistogramTooltipDataPoint[][];

  return { closestBin, closestBinsByClosestOne, closestBinsGrouped };
}
